import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ShoppingBag, ArrowRight, Loader2 } from 'lucide-react'
import { auth } from '../lib/firebase'
import { deriveEcUrl, ecEntryUrl, hasEcNext, rememberEcNext } from '../lib/ecUrl'
import { getDeviceId } from '../lib/deviceId'

/**
 * YOKOSO PAY Home に表示する EC への入口カード。
 *
 * クリック時に services/pay/api 経由で Firebase Custom Token を発行し、
 * EC URL に ?handoff=<token> で渡して同じタブで遷移。EC 側 (HandoffConsumer) が
 * 自動でログインを完了させる (#879 / #880 EC <-> YOKOSO PAY SSO)。
 *
 * VITE_EC_URL が未設定 + preview パターン外 (= demo/prod 初期等) ではカードを描画しない。
 *
 * #2237 ショップの「YOKOSO PAY から入り直す」(`/home?ec_next=<ショップの画面>`) から
 * 来たときは、戻り先を覚えて**カードに「ショップに戻ってお支払いを続ける」と出す**。
 * 押すとショップの入口に印を付けて渡し、ショップがログインし終えてから元の画面へ移る。
 * PAY から入るとショップのこの端末が信頼済みになり、払えるようになる (#1228)。
 *
 * **押さずに自動では戻さない。** 開いた直後は PAY 側の端末の確認やパスキーの登録の
 * 判定がまだ終わっていないことがあり、自動で移るとそれらの確認を飛ばしてショップに
 * 入れてしまう。戻り先は sessionStorage に置くので、ログインやそれらの確認を挟んでも残る。
 */
export default function EcEntryCard() {
  const ecUrl = deriveEcUrl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [returning, setReturning] = useState(() => (ecUrl ? hasEcNext() : false))

  // #2237 URL の ec_next を覚えて URL から消す (router 経由なので location も揃う)
  const rawNext = searchParams.get('ec_next')
  useEffect(() => {
    if (rawNext === null) return
    if (ecUrl && rememberEcNext(rawNext)) setReturning(true)
    const rest = new URLSearchParams(searchParams)
    rest.delete('ec_next')
    setSearchParams(rest, { replace: true })
  }, [rawNext, ecUrl, searchParams, setSearchParams])

  if (!ecUrl) return null

  const handleClick = async () => {
    const user = auth.currentUser
    if (!user) {
      // 未ログインなら token なしで遷移。EC 側で自前ログインを促す。
      window.location.assign(ecEntryUrl(ecUrl).toString())
      return
    }
    setBusy(true)
    setError(null)
    try {
      const idToken = await user.getIdToken()
      const res = await fetch('/api/v1/ec/handoff_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        // #1227: 信頼済み端末からのみ EC へ handoff できるようにする
        // (EC は非対面で不正の旨味が大きいため、出所を信頼済み端末に限定)。
        body: JSON.stringify({ device_id: getDeviceId() }),
      })
      if (res.status === 401) {
        // #2237 PAY のこの端末がまだ確認されていない。番号だけ出しても次に何をすればいいか分からない
        setError('この端末はまだ確認されていません。端末の確認を済ませてから押してください / This device is not verified yet. Please verify it first.')
        setBusy(false)
        return
      }
      if (!res.ok) throw new Error(`handoff_token ${res.status}`)
      const body = (await res.json()) as { custom_token: string }
      // #2237 印はショップの入口 (ログイン不要) に付ける。戻り先は `next` で渡す
      const u = ecEntryUrl(ecUrl)
      u.searchParams.set('handoff', body.custom_token)
      window.location.assign(u.toString())
    } catch (e) {
      setError(`エラー: ${e instanceof Error ? e.message : String(e)}`)
      setBusy(false)
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      data-testid="ec-entry-card"
      className={`tap-card mx-4 mt-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl bg-gradient-to-br from-yokoso-red to-yokoso-red-dark p-4 text-left text-white shadow-card disabled:opacity-70 ${
        returning ? 'ring-4 ring-gold/70' : ''
      }`}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
        {busy ? <Loader2 size={22} className="animate-spin" /> : <ShoppingBag size={22} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-base">お土産ショップ / Souvenir shop</div>
        <div className="text-xs opacity-90">
          {returning
            ? 'ショップに戻ってお支払いを続ける / Return to the shop to pay'
            : 'ホテル / コンビニ / 空港でお受け取り — ワンタップでログイン'}
        </div>
        {error && <div className="mt-1 text-[10px] opacity-80">{error}</div>}
      </div>
      <ArrowRight size={20} className="shrink-0 opacity-90" />
    </button>
  )
}
